import { BillingLoading } from 'components/lab/billingEraReports/BillingLoading'
import { BillingTransactionClaims } from 'components/lab/billingEraReports/BillingTransactionClaims'
import { EraClaimSelection } from 'components/lab/billingEraReports/types'
import { formatCalendarDate } from 'helpers/dates'
import { BillingEraPaymentSummary, BillingEraReportsListQuery, BillingEraTransaction } from 'store/types'
import React from 'react'
import { useGetEraTransactionsQuery } from 'store/services/billingCharges'

const Money = ({ v }: { v?: string | number }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)

interface Props {
  eraId: string
  query: BillingEraReportsListQuery
  selection?: EraClaimSelection
}

export const BillingEraTransactions = ({ eraId, query, selection }: Props) => {
  const { data: transactions, isLoading } = useGetEraTransactionsQuery({
    id: eraId,
    query,
  })

  return (
    <div className="p-2">
      {isLoading ? (
        <BillingLoading />
      ) : (
        transactions?.map((t, ti) => (
          <TransactionRow
            transaction={t}
            index={ti}
            key={ti}
            eraId={eraId}
            query={query}
            selection={selection}
            labelled={transactions.length > 1}
          />
        ))
      )}
    </div>
  )
}

interface PaymentSummaryProps {
  payment: BillingEraPaymentSummary
  // Numbered only when the file holds more than one payment.
  index?: number
}

// One payment of an ERA file: who paid, how, how much and when.
const EraPaymentSummary = ({ payment, index }: PaymentSummaryProps) => (
  <div className="d-flex align-items-center flex-wrap">
    {index !== undefined && <div className="mr-3 text-muted">#{index + 1}</div>}
    <div style={{ fontWeight: 600 }}>{payment.payerName || 'Unknown Payer'}</div>
    <div className="ml-3">
      <span className={'text-muted'}>{payment.paymentMethod || 'NON'}</span> {payment.paymentMethodIdentifier || '-'}
    </div>
    <div className="ml-3">
      <span className={'text-muted'}>Check</span> <Money v={payment.checkAmount} />
    </div>
    <div className="ml-3">
      <span className={'text-muted'}>Check Date</span> {formatCalendarDate(payment.checkDate)}
    </div>
    <div className="ml-3">
      <span className={'text-muted'}>Claims</span> {payment.claimsAmount}
    </div>
    <div className="ml-3">
      <span className={'text-muted'}>Check Number (Trace Number)</span> {payment.checkNumber || '-'}
    </div>
  </div>
)

interface TransactionProps {
  transaction: BillingEraTransaction
  eraId: string
  index: number
  query: BillingEraReportsListQuery
  selection?: EraClaimSelection
  // The payment summary sits on the file's header line; inside the file it
  // only labels each payment's claims when there are several.
  labelled?: boolean
}

export const TransactionRow = ({ transaction, eraId, index, query, selection, labelled }: TransactionProps) => {
  return (
    <div className="mb-3">
      {labelled && (
        <div
          className="d-flex align-items-center justify-content-between p-3"
          style={{ background: 'rgba(224,228,230,0.6)', border: '1px solid #e9ecef' }}>
          <EraPaymentSummary payment={transaction} index={index} />
        </div>
      )}

      <BillingTransactionClaims
        transactionId={transaction.id}
        eraId={eraId}
        query={query}
        selection={selection}
      />
    </div>
  )
}
